import { Loader2, Clock, Edit, PlusCircle, ArrowRightLeft, Trash2, CheckCircle } from "lucide-react";
import { format } from "date-fns";

import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { trpc, DATA_MUTATED_EVENT } from "@/lib/trpc";
import { useEffect } from "react";
import { tryResolveStatusLabel } from "@/config/status-registry";
import { getUploadUrl } from "@/lib/utils";

interface ActivityLog {
    id: string;
    entityType: string;
    entityId: string;
    action: string;
    field?: string | null;
    oldValue?: string | null;
    newValue?: string | null;
    description?: string | null;
    userId?: string | null;
    createdAt: Date | string;
    user?: {
        id: string;
        fullName: string;
        email?: string | null;
        avatar?: string | null;
        role?: string | null;
    } | null;
}

interface ActivityLogSectionProps {
    entityId: string;
    entityType: string;
    variant?: "card" | "inline"; // card = wrapped in Card, inline = bare content
}

const actionIcons: Record<string, React.ReactNode> = {
    created: <PlusCircle className="h-3.5 w-3.5 text-emerald-500" />,
    create: <PlusCircle className="h-3.5 w-3.5 text-emerald-500" />,
    updated: <Edit className="h-3.5 w-3.5 text-blue-500" />,
    update: <Edit className="h-3.5 w-3.5 text-blue-500" />,
    status_changed: <ArrowRightLeft className="h-3.5 w-3.5 text-amber-500" />,
    update_status: <ArrowRightLeft className="h-3.5 w-3.5 text-amber-500" />,
    deleted: <Trash2 className="h-3.5 w-3.5 text-rose-500" />,
    delete: <Trash2 className="h-3.5 w-3.5 text-rose-500" />,
    commented: <CheckCircle className="h-3.5 w-3.5 text-indigo-500" />,
    approved: <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />,
    approve: <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />,
    attachment_added: <PlusCircle className="h-3.5 w-3.5 text-indigo-500" />,
    restore: <ArrowRightLeft className="h-3.5 w-3.5 text-emerald-500" />,
};

const actionLabels: Record<string, string> = {
    created: "đã tạo mới",
    create: "đã tạo mới",
    updated: "đã cập nhật",
    update: "đã cập nhật",
    status_changed: "đã thay đổi trạng thái",
    update_status: "đã thay đổi trạng thái",
    deleted: "đã xóa",
    delete: "đã xóa",
    commented: "đã bình luận",
    approved: "đã duyệt",
    approve: "đã duyệt",
    attachment_added: "đã đính kèm tệp",
    restore: "đã khôi phục",
    complete: "đã hoàn tất",
    cancel: "đã hủy",
    reject: "đã từ chối",
};

/** Nhãn trường hiển thị khi log có `field` (server đã ghi nhãn sẵn trong mô tả cho các trường khác) */
const fieldLabels: Record<string, string> = {
    status: "Trạng thái",
    paymentStatus: "Trạng thái thanh toán",
    priority: "Độ ưu tiên",
    result: "Kết quả",
};

/** Dòng do middleware cũ tự ghi ("Thực hiện updateStatus trên purchase_order") — không có thông tin */
const GENERIC_DESC = /^Thực hiện \w+ trên /;
const STATUS_ACTIONS = new Set(['status_changed', 'update_status']);

function resolveActionLabel(action: string): string {
    const key = (action || '').toLowerCase();
    return actionLabels[key] || actionLabels[action] || action;
}

function resolveActionIcon(action: string): React.ReactNode {
    const key = (action || '').toLowerCase();
    return actionIcons[key] || actionIcons[action] || <Clock className="h-3 w-3 text-slate-400" />;
}



import { useInfiniteQuery } from '@tanstack/react-query';

export default function ActivityLogSection({ entityId, entityType, variant = "card" }: ActivityLogSectionProps) {
    const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } = useInfiniteQuery(
        trpc.activityLog.getByEntity.infiniteQueryOptions(
            { entityType, entityId, limit: 50 },
            { 
                getNextPageParam: (lastPage: any) => lastPage.nextCursor,
                enabled: !!entityId && entityId !== "Đang tải..."
            }
        )
    );

    // Sau mỗi thao tác ghi dữ liệu thành công → nạp lại lịch sử (server ghi log trước khi trả kết quả)
    useEffect(() => {
        if (!entityId || entityId === "Đang tải...") return;
        const onMutated = () => { void refetch(); };
        window.addEventListener(DATA_MUTATED_EVENT, onMutated);
        return () => window.removeEventListener(DATA_MUTATED_EVENT, onMutated);
    }, [entityId, refetch]);

    const logs = data?.pages.flatMap((page: any) => page.items as any as ActivityLog[]) || [];

    const filteredLogs = logs.filter((log: ActivityLog) => !['commented', 'attachment_added'].includes((log.action || '').toLowerCase()));

    const content = (
        <>
            {isLoading ? (
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
            ) : filteredLogs.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-500" data-testid="activity-log-empty">Chưa có lịch sử cập nhật.</p>
            ) : (
                <div className="relative space-y-0 max-h-[400px] overflow-y-auto pr-2" data-testid="activity-log-list">
                    {/* Timeline line */}
                    <div className="absolute left-[15px] top-2 bottom-2 w-px bg-slate-200" />

                    {filteredLogs.map((log: ActivityLog, index: number) => {
                        const userName = log.user?.fullName || (log.user?.email ? log.user.email.split('@')[0] : null) || "Hệ thống";
                        const actionKey = (log.action || '').toLowerCase();
                        const actionText = resolveActionLabel(log.action);
                        const timeStr = format(new Date(log.createdAt), "dd/MM/yyyy HH:mm");

                        // Trường thay đổi: trạng thái luôn hiển thị "Trạng thái: A → B" với nhãn tiếng Việt
                        const isStatusField = log.field === 'status' || (STATUS_ACTIONS.has(actionKey) && !log.field);
                        const fieldName = log.field ? (fieldLabels[log.field] || log.field) : (isStatusField ? 'Trạng thái' : null);
                        const translate = (v?: string | null) => (v && (isStatusField || /status$/i.test(log.field || '')) ? tryResolveStatusLabel(v) : v);
                        const displayOld = translate(log.oldValue);
                        const displayNew = translate(log.newValue);
                        const isCreate = ['create', 'created'].includes(actionKey);

                        return (
                            <div key={log.id || index} className="relative flex gap-3 py-2.5 group" data-testid="activity-log-item" data-action={actionKey}>
                                {/* Timeline dot */}
                                <div className="relative z-10 flex-shrink-0 w-7 h-7 flex items-center justify-center">
                                    <div className="w-6 h-6 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center group-hover:border-indigo-300 transition-colors">
                                        {resolveActionIcon(log.action)}
                                    </div>
                                </div>

                                {/* Single-line content */}
                                <div className="flex-1 min-w-0">
                                    <p className="text-[13px] text-slate-700 leading-relaxed break-words">
                                        <span className="font-semibold text-slate-900">{userName}</span>
                                        {' '}
                                        <span className="text-slate-500">{actionText}</span>
                                        <span className="text-[10px] text-slate-400 font-mono ml-2 whitespace-nowrap">
                                            {timeStr}
                                        </span>
                                    </p>
                                    {fieldName && (displayOld || displayNew) && (
                                        <div className="mt-1 text-[13px] text-slate-600 pl-2 border-l-2 border-slate-200 flex flex-wrap items-center gap-1.5" data-testid="activity-log-change">
                                            <span className="font-medium text-slate-700">{isCreate ? `${fieldName} ban đầu` : fieldName}:</span>
                                            {displayOld && !isCreate && (
                                                <span className="px-1.5 py-0.5 bg-rose-50 text-rose-700 rounded text-xs line-through">{displayOld}</span>
                                            )}
                                            {displayOld && !isCreate && <span className="text-slate-400 text-xs">→</span>}
                                            {!displayOld && !isCreate && <span className="text-slate-400 text-xs">→</span>}
                                            {displayNew && (
                                                <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs font-semibold">{displayNew}</span>
                                            )}
                                            {(log as any).inferred && (
                                                <span className="text-[10px] text-slate-400" title="Bản ghi cũ không lưu giá trị — suy ra từ các lần đổi trạng thái liền kề">(suy ra)</span>
                                            )}
                                        </div>
                                    )}
                                    {log.description && !GENERIC_DESC.test(log.description) && !(STATUS_ACTIONS.has(actionKey) && /^Chuyển trạng thái/.test(log.description)) && (
                                        <div className="mt-1.5">
                                            {(() => {
                                                const desc = log.description;
                                                
                                                const renderTextWithLinks = (text: string) => {
                                                    // Parse Markdown links [text](url) and images ![alt](url)
                                                    const mdRegex = /(\!?\[.*?\]\(.*?\))/g;
                                                    const parts = text.split(mdRegex);
                                                    
                                                    return parts.map((part, index) => {
                                                        const mdMatch = part.match(/^!?\[(.*?)\]\((.*?)\)$/);
                                                        if (mdMatch) {
                                                            const [, linkText, url] = mdMatch;
                                                            return (
                                                                <a key={index} href={getUploadUrl(url)} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline font-medium inline-flex items-center gap-1">
                                                                    {linkText}
                                                                </a>
                                                            );
                                                        }
                                                        
                                                        // Parse raw URLs in the remaining text
                                                        const urlRegex = /(https?:\/\/[^\s]+)/g;
                                                        const rawParts = part.split(urlRegex);
                                                        return rawParts.map((rawPart, rawIndex) => {
                                                            if (rawPart.match(urlRegex)) {
                                                                return (
                                                                    <a key={`${index}-${rawIndex}`} href={getUploadUrl(rawPart)} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline font-medium inline-flex items-center gap-1 break-all">
                                                                        {rawPart.length > 50 ? rawPart.substring(0, 50) + '...' : rawPart}
                                                                    </a>
                                                                );
                                                            }
                                                            return <span key={`${index}-${rawIndex}`}>{rawPart}</span>;
                                                        });
                                                    });
                                                };

                                                const renderValue = (val: string) => {
                                                    if (!val) return val;
                                                    
                                                    // Handle markdown images/links like ![alt](url) or [text](url)
                                                    const mdMatch = val.match(/^!?\[(.*?)\]\((.*?)\)$/);
                                                    if (mdMatch) {
                                                        const [, linkText, url] = mdMatch;
                                                        return (
                                                            <a href={getUploadUrl(url)} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                                <span className="truncate max-w-[150px]">{linkText || 'Link'}</span>
                                                            </a>
                                                        );
                                                    }

                                                    if (val.startsWith('http://') || val.startsWith('https://')) {
                                                        return (
                                                            <a href={val} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                                <span className="truncate max-w-[150px]">{val.split('/').pop() || 'Link'}</span>
                                                            </a>
                                                        );
                                                    }
                                                    return val;
                                                };

                                                if (desc?.includes('từ [') && desc?.includes('] sang [')) {
                                                    // mỗi trường "X: từ [a] sang [b]" và mỗi dòng con (tách bằng xuống dòng) là một mục
                                                    const parts = desc.split(/\n|, (?=[^,:]+: từ \[)/).filter((p: string) => p.trim());
                                                    return (
                                                        <ul className="space-y-1.5">
                                                            {parts.map((part: string, i: number) => {
                                                                const match = part.match(/(.*?): từ \[(.*?)\] sang \[(.*?)\]/);
                                                                if (match) {
                                                                    const [_, field, oldVal, newVal] = match;
                                                                    return (
                                                                        <li key={i} className="text-[13px] text-slate-600 pl-2 border-l-2 border-slate-200 flex flex-wrap items-center gap-1.5">
                                                                            <span className="font-medium text-slate-700">{field}:</span>
                                                                            <span className="px-1.5 py-0.5 bg-rose-50 text-rose-600 rounded text-xs line-through max-w-[200px] truncate flex items-center" title={oldVal}>{renderValue(oldVal)}</span>
                                                                            <span className="text-slate-400 text-xs">→</span>
                                                                            <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded text-xs font-semibold max-w-[200px] truncate flex items-center" title={newVal}>{renderValue(newVal)}</span>
                                                                        </li>
                                                                    );
                                                                }
                                                                return (
                                                                    <li key={i} className="text-[13px] text-slate-600 pl-2 border-l-2 border-slate-200">
                                                                        {renderTextWithLinks(part)}
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    );
                                                }
                                                return (
                                                    <p className="text-[13px] text-slate-600 pl-2 border-l-2 border-slate-200 whitespace-pre-wrap">
                                                        {desc ? renderTextWithLinks(desc) : null}
                                                    </p>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {hasNextPage && (
                        <div className="pt-4 pb-2 flex justify-center">
                            <button
                                onClick={() => fetchNextPage()}
                                disabled={isFetchingNextPage}
                                className="text-xs font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded transition-colors disabled:opacity-50"
                            >
                                {isFetchingNextPage ? "Đang tải..." : "Tải thêm"}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </>
    );

    if (variant === "inline") {
        return content;
    }

    return (
        <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 border-b border-slate-50">
                <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-4 w-4 text-slate-500" />
                    Lịch sử thay đổi
                    <span className="text-xs font-normal text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                        {filteredLogs.length}
                    </span>
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
                {content}
            </CardContent>
        </Card>
    );
}
